import { Fragment, FunctionalComponent, h } from 'preact';
import { useState } from 'preact/hooks';
import { Offer, SessionProduct } from '../mock/types';

/** 价格渲染：null=「价格待公布」（T16，禁止 ¥null）。原型布局：灰色前缀+红色大数字+单位 */
export function OfferPrice({ price, unit, prefix }: { price: string | null; unit: string; prefix?: string }) {
  if (price === null) {
    return <span class="vp-price-pending">价格待公布</span>;
  }
  return (
    <span class="vp-price-value">
      {prefix ? <span class="vp-price-unit">{prefix}</span> : null}
      <b class="vp-price">{price}</b>
      <span class="vp-price-unit">元/{unit}</span>
    </span>
  );
}

/** 单个规格块（视频形态）：规格名「」+（有赠品时）赠品橙块[赠品标+图+数量拍N红字+清单] + 价格行 */
export function OfferBlock({ offer }: { offer: Offer }) {
  const qty = offer.buyCount > 1 ? offer.buyCount : 1;
  return (
    <div class="vp-offer">
      <div class="vp-offer-name">{offer.name}</div>
      {offer.gift.has ? (
        <div class={`vp-gift${offer.gift.images[0] ? '' : ' vp-gift-noimage'}`}>
          <span class="vp-gifttag">赠品</span>
          {offer.gift.images[0] ? <img class="vp-gift-img" src={offer.gift.images[0]} alt="" /> : null}
          <span class="vp-gift-meta">
            <span class="vp-gift-qty">数量拍{qty}</span>
            <span class="vp-gift-desc">{offer.gift.desc}</span>
          </span>
        </div>
      ) : null}
      {offer.condition && (!offer.gift.has || offer.condition.trim() !== offer.gift.desc?.trim()) ? <span class="vp-offer-conditions">{offer.condition}</span> : null}
      <div class="vp-offer-price">
        <span class="vp-price-unit">数量拍{qty}，到手价：</span>
        <OfferPrice price={offer.price} unit={offer.toHandUnit} />
      </div>
    </div>
  );
}

interface CardProps {
  product: SessionProduct;
  onOpenDetail: (p: SessionProduct) => void;
  /** 场次状态：ended→已开播胶囊，pending→上播提醒 */
  liveFlag?: 'ended' | 'pending' | 'living';
}

/** 李佳琦主赠左右卡（L05·视频形态）：左主图+链接序号+竖分隔线+右侧标题/规格/赠品/条件/价格 */
export const ProductCardMain: FunctionalComponent<CardProps> = ({ product, onOpenDetail }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleOffers = expanded ? product.offers : product.offers.slice(0, 2);
  const expandable = product.offers.length > 2;
  return (
    <div>
      <div class="vp-productcard">
        <div class="vp-productcard-left">
          <button class="vp-productcard-figure" onClick={() => onOpenDetail(product)}>
            <img src={product.image} alt={product.itemName} />
            <span class="vp-linkno">{product.linkNo}号链接</span>
          </button>
          <span class="vp-main-caption">主商品</span>
        </div>
        <div class="vp-productcard-right">
          <button class="vp-productcard-title" onClick={() => onOpenDetail(product)}>
            {product.itemName}
          </button>
          <div>
            {visibleOffers.map((o) => <OfferBlock key={o.specId} offer={o} />)}
          </div>
          {expandable && (
            <button type="button" class="vp-offer-more" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
              {expanded ? '收起规格信息' : `展开全部${product.offers.length}种规格`}
              <i class={`more-arrow${expanded ? ' open' : ''}`} aria-hidden="true" />
            </button>
          )}
          <div class="vp-card-actions">
            <button type="button" class="vp-card-btn solid" onClick={() => onOpenDetail(product)}>
              查看商品详情
              <i class="vp-card-detail-arrow" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** 紧凑商品行（L06）：图+序号在左，标题/权益/分类/价格/看详情在右 */
export const ProductRow: FunctionalComponent<CardProps> = ({ product, onOpenDetail }) => {
  const best = product.offers[0];
  const perks =
    best && best.gift.has
      ? `赠${best.gift.desc}`
      : best?.name ?? '信息待补全';
  return (
    <div class="vp-productrow">
      <button class="vp-productcard-figure" onClick={() => onOpenDetail(product)}>
        <img src={product.image} alt={product.itemName} />
        <span class="vp-linkno">{product.linkNo}号</span>
      </button>
      <div class="vp-productrow-body">
        <button class="vp-productrow-title vp-productcard-title" onClick={() => onOpenDetail(product)}>
          {product.itemName}
        </button>
        <span class="vp-productrow-perks">{perks}</span>
        <div class="vp-price-row">
          <span class="vp-price-unit">拍{best?.buyCount ?? 1}件 · 预告价</span>
          {best?.price ? (
            <b class="vp-price">{best.price}<i class="vp-price-inline-unit">元/{best.toHandUnit}</i></b>
          ) : (
            <span class="vp-price-pending">价格待公布</span>
          )}
        </div>
        <button class="vp-card-btn compact solid" onClick={() => onOpenDetail(product)}>看详情</button>
      </div>
    </div>
  );
};
